// src/components/ui/BookingModal.tsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Clock, User, Phone, Mail } from 'lucide-react';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedService?: string;
}

const BookingModal: React.FC<BookingModalProps> = ({ isOpen, onClose, selectedService }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    date: '',
    time: '',
    service: selectedService || '',
    notes: ''
  });

  const timeSlots = [
    '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', 
    '15:00', '16:00', '17:00'
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In production, this would connect to a booking API
    console.log('Booking submitted:', formData);
    alert('Booking request submitted! We will contact you shortly to confirm.');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            {/* Modal */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-mauve-100 p-6 rounded-t-2xl">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-mauve-900">Book Appointment</h2>
                    <p className="text-mauve-600">Get Nailed by AJ - Accra</p>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-mauve-500 hover:text-mauve-700 transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-mauve-700 mb-2">
                    <div className="flex items-center">
                      <User size={16} className="mr-2" />
                      Full Name
                    </div>
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-mauve-200 rounded-lg focus:border-mauve-500 focus:ring-2 focus:ring-mauve-100 outline-none transition"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-mauve-700 mb-2">
                      <div className="flex items-center">
                        <Phone size={16} className="mr-2" />
                        Phone
                      </div>
                    </label>
                    <input
                      type="tel"
                      required
                      className="w-full px-4 py-2 border border-mauve-200 rounded-lg focus:border-mauve-500 focus:ring-2 focus:ring-mauve-100 outline-none transition"
                      placeholder="020 XXX XXXX"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-mauve-700 mb-2">
                      <div className="flex items-center">
                        <Mail size={16} className="mr-2" />
                        Email
                      </div>
                    </label>
                    <input
                      type="email"
                      required
                      className="w-full px-4 py-2 border border-mauve-200 rounded-lg focus:border-mauve-500 focus:ring-2 focus:ring-mauve-100 outline-none transition"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-mauve-700 mb-2">
                      <div className="flex items-center">
                        <Calendar size={16} className="mr-2" />
                        Date
                      </div>
                    </label>
                    <input
                      type="date"
                      required
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 border border-mauve-200 rounded-lg focus:border-mauve-500 focus:ring-2 focus:ring-mauve-100 outline-none transition"
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-mauve-700 mb-2">
                      <div className="flex items-center">
                        <Clock size={16} className="mr-2" />
                        Time
                      </div>
                    </label>
                    <select
                      required
                      className="w-full px-4 py-2 border border-mauve-200 rounded-lg focus:border-mauve-500 focus:ring-2 focus:ring-mauve-100 outline-none transition"
                      value={formData.time}
                      onChange={(e) => setFormData({...formData, time: e.target.value})}
                    >
                      <option value="">Select time</option>
                      {timeSlots.map((time) => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-mauve-700 mb-2">
                    Additional Notes
                  </label>
                  <textarea
                    className="w-full px-4 py-2 border border-mauve-200 rounded-lg focus:border-mauve-500 focus:ring-2 focus:ring-mauve-100 outline-none transition min-h-[100px]"
                    placeholder="Any special requests or notes..."
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="w-full btn-primary py-3 text-lg font-semibold"
                >
                  Request Booking
                </button>

                {/* Note */}
                <p className="text-center text-sm text-mauve-600">
                  We will contact you within 2 hours to confirm your appointment.
                </p>
              </form>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default BookingModal;